import { useEffect, useRef } from 'react';
import { showTooltip, hideTooltip } from './Tooltip';
import { formatAxis, formatWithUnit } from '../../lib/fmt';

export type LineSeries = { key: string; label: string; color?: string; dash?: string };

const DEFAULT_COLORS = [
  'var(--chart-c1)',
  'var(--chart-c3)',
  'var(--chart-c4)',
  'var(--chart-c5)',
  'var(--chart-c6)',
];
const DEFAULT_DASH = [undefined, '6 4', '2 4', '10 4 2 4']; // plein puis pointillés

export default function LineChart({
  data,
  xKey,
  yKey,
  series,
  height = 260,
  color = 'var(--chart-c1)',
  xLabelFormat,
  unit,
}: {
  data: Array<Record<string, any>>;
  xKey: string;
  /** Clé Y pour le mode "une seule série" (legacy). Ignoré si `series` est fourni. */
  yKey?: string;
  /** Plusieurs séries. Si omis, on fallback sur `yKey`. */
  series?: LineSeries[];
  height?: number;
  color?: string;
  xLabelFormat?: (v: any) => string;
  unit?: string | null;
}) {
  const safeData: Array<Record<string, any>> = Array.isArray(data) ? data.filter(Boolean) : [];

  // Normalise `series` (au moins une série)
  const effectiveSeries: LineSeries[] =
    series && series.length > 0
      ? series.map((s, i) => ({
          ...s,
          color: s.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length],
          dash: s.dash ?? DEFAULT_DASH[i % DEFAULT_DASH.length],
        }))
      : [
          {
            key: yKey ?? 'y',
            label: yKey ?? 'Valeur',
            color,
            dash: undefined,
          },
        ];

  const width = 560;
  const padL = 56;
  const padR = 16;
  const padT = 28;
  const padB = 36;
  const w = width - padL - padR;
  const h = height - padT - padB;

  if (safeData.length === 0) {
    return (
      <svg width="100%" viewBox={`0 0 ${width} ${height}`}>
        <text x={width / 2} y={height / 2} textAnchor="middle" fontSize="11" style={{ fill: 'var(--chart-text-muted)' }}>
          Aucune donnée
        </text>
      </svg>
    );
  }

  // Max/min sur l'ensemble des séries pour un axe unique
  let maxV = 0;
  let minV = 0;
  for (const d of safeData) {
    for (const s of effectiveSeries) {
      const v = Number(d[s.key]) || 0;
      if (v > maxV) maxV = v;
      if (v < minV) minV = v;
    }
  }
  if (minV === 0 && maxV === 0) maxV = 1;

  const allInts = safeData.every((d) =>
    effectiveSeries.every((s) => {
      const v = Number(d[s.key]);
      return Number.isFinite(v) && Math.abs(v - Math.round(v)) < 1e-6;
    })
  );

  const range = maxV - minV || 1;
  const vToY = (v: number) => padT + ((maxV - v) / range) * h;
  const step = safeData.length > 1 ? w / (safeData.length - 1) : w;

  const pathsRef = useRef<SVGSVGElement>(null);
  useEffect(() => {
    if (!pathsRef.current) return;
    const paths = pathsRef.current.querySelectorAll<SVGPathElement>('path[data-line]');
    paths.forEach((p) => {
      const len = p.getTotalLength?.() ?? 500;
      p.style.transition = 'none';
      p.style.strokeDasharray = `${len}`;
      p.style.strokeDashoffset = `${len}`;
      requestAnimationFrame(() => {
        p.style.transition = 'stroke-dashoffset 900ms ease';
        p.style.strokeDashoffset = '0';
      });
    });
  }, [safeData.map((d) => d[xKey]).join(','), effectiveSeries.map((s) => s.key).join(',')]);

  // Ticks Y
  const tickValues = buildTicks(minV, maxV, 5, allInts);

  const xTicksCount = Math.min(8, safeData.length);
  const xTickIdx = Array.from({ length: xTicksCount }, (_, i) =>
    Math.round((i * (safeData.length - 1)) / Math.max(1, xTicksCount - 1))
  );

  return (
    <svg ref={pathsRef} width="100%" viewBox={`0 0 ${width} ${height}`} style={{ overflow: 'visible', fontFamily: 'inherit' }}>
      {/* Légende */}
      {effectiveSeries.length > 0 && (effectiveSeries.length > 1 || effectiveSeries[0].label) && (
        <g transform={`translate(${padL} 4)`}>
          {effectiveSeries.map((s, i) => (
            <g key={s.key} transform={`translate(${i * 130} 0)`}>
              <line x1={0} x2={18} y1={6} y2={6} style={{ stroke: s.color }} strokeWidth="2.4" strokeDasharray={s.dash} />
              <text x={24} y={9} fontSize="11" style={{ fill: 'var(--chart-text)' }}>
                {s.label}
              </text>
            </g>
          ))}
        </g>
      )}

      {/* Grille Y */}
      {tickValues.map((v, i) => {
        const y = vToY(v);
        return (
          <g key={i}>
            <line x1={padL} x2={width - padR} y1={y} y2={y} style={{ stroke: 'var(--chart-grid)' }} strokeDasharray={v === 0 ? undefined : '2 3'} />
            <text x={padL - 6} y={y + 3} textAnchor="end" fontSize="10" style={{ fill: 'var(--chart-text-muted)' }}>
              {formatAxis(v, unit)}
            </text>
          </g>
        );
      })}

      {/* Courbes */}
      {effectiveSeries.map((s) => {
        const pts = safeData.map((d, i) => ({
          x: padL + i * step,
          y: vToY(Number(d[s.key]) || 0),
          raw: d,
        }));
        const pathD = pts.map((p, i) => (i === 0 ? 'M' : 'L') + p.x.toFixed(1) + ' ' + p.y.toFixed(1)).join(' ');
        // Zone de fond seulement pour la première série (pleine)
        const isFirst = s === effectiveSeries[0] && !s.dash;
        const fillPath = isFirst
          ? pathD + ` L ${padL + (pts.length - 1) * step} ${vToY(0)} L ${padL} ${vToY(0)} Z`
          : null;
        return (
          <g key={s.key}>
            {fillPath && <path d={fillPath} style={{ fill: s.color }} opacity={0.1} />}
            <path
              data-line
              d={pathD}
              fill="none"
              style={{ stroke: s.color }}
              strokeWidth={2.4}
              strokeLinecap="round"
              strokeDasharray={s.dash}
            />
            {pts.map((p, i) => (
              <circle
                key={i}
                cx={p.x}
                cy={p.y}
                r={3.5}
                fill="#fff"
                strokeWidth={2}
                style={{ stroke: s.color, cursor: 'crosshair' }}
                onMouseMove={(e) =>
                  showTooltip({
                    x: e.clientX,
                    y: e.clientY,
                    title: xLabelFormat ? xLabelFormat(p.raw[xKey]) : String(p.raw[xKey]),
                    rows: effectiveSeries.map((ss) => ({
                      label: ss.label,
                      value: formatWithUnit(Number(p.raw[ss.key] ?? 0), unit),
                    })),
                  })
                }
                onMouseLeave={hideTooltip}
              />
            ))}
          </g>
        );
      })}

      {/* Ticks X */}
      {xTickIdx.map((idx, i) => {
        const row = safeData[idx];
        if (!row) return null;
        const x = padL + idx * step;
        const raw = row[xKey];
        return (
          <text key={i} x={x} y={height - 14} textAnchor="middle" fontSize="10" style={{ fill: 'var(--chart-text-muted)' }}>
            {xLabelFormat ? xLabelFormat(raw) : String(raw ?? '')}
          </text>
        );
      })}

      {/* Ligne du zéro */}
      {minV < 0 && (
        <line x1={padL} x2={width - padR} y1={vToY(0)} y2={vToY(0)} style={{ stroke: 'var(--chart-text)' }} strokeWidth={1} />
      )}
    </svg>
  );
}

function buildTicks(min: number, max: number, approx: number, allInts: boolean): number[] {
  if (min === max) return [min];
  const range = max - min;
  if (allInts && range <= 10) {
    const out: number[] = [];
    const start = Math.floor(min);
    const end = Math.ceil(max);
    for (let v = start; v <= end; v++) out.push(v);
    return out;
  }
  const rawStep = range / approx;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const candidates = allInts ? [1, 2, 5, 10] : [1, 2, 2.5, 5, 10];
  let step = candidates.map((c) => c * mag).find((s) => range / s <= approx * 1.6) ?? mag;
  if (allInts) step = Math.max(1, Math.round(step));
  const start = Math.floor(min / step) * step;
  const end = Math.ceil(max / step) * step;
  const out: number[] = [];
  for (let v = start; v <= end + 1e-9; v += step) {
    out.push(Math.round(v * 1e6) / 1e6);
  }
  return out;
}
